import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, Navigate } from 'react-router-dom';
import { isAdminSelector, loadingSelector, sagaDataLoadingSelector } from '@/domain/env';
import {
  allCompaniesByOrganizationSelector,
  companiesByOrganizationSelector,
  companiesWorkSpaceTypeSelector,
} from '@/domain/companies';
import ROUTES_PATH from '@/router/routesPathConfig';
import useToggle from '@/hooks/useToggle';

import Container from '@mui/material/Container';
import LayoutCompanies from '@/components/mui/Layouts/Companies';
import StickySubheader from '@/components/mui/Layouts/components/StickySubheader';
import OrganizationModal from '@/pages/common/Dialog/DialogCreateOrganization';
import TileView from '@/pages/companies/components/TileView';
import GridView from '@/pages/companies/components/GridView';
import CompaniesTitle from '@/pages/companies/components/Title';
import Bar from '@/pages/companies/components/Bar';
import CreateCompany from '@/pages/companies/components/CreateCompany';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';

interface GridViewHandle {
  exportToExcel: () => void;
}

const Companies: React.FC = () => {
  const allCompanies = useSelector(allCompaniesByOrganizationSelector);
  const currentCompanyList = useSelector(companiesByOrganizationSelector);
  const isAdmin = useSelector(isAdminSelector);
  const loading = useSelector(loadingSelector);
  const isSagaDataLoading = useSelector(sagaDataLoadingSelector);
  const companiesWorkSpaceType = useSelector(companiesWorkSpaceTypeSelector);
  const [isOpenCompanyModal, toggleCompanyModal] = useToggle();
  const isGridView = companiesWorkSpaceType === 'grid';
  const gridRef = useRef<GridViewHandle>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);

  if (allCompanies.size === 1 && !isAdmin) {
    const ROUTE_ID = ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.id;
    const companyId = allCompanies.first().get('id');
    const to = generatePath(ROUTES_PATH[ROUTE_ID].absolute, { companyId });

    return <Navigate to={to} />;
  }

  return (
    <>
      <LayoutCompanies>
        <StickySubheader>
          <CompaniesTitle onClickAdd={toggleCompanyModal} companiesCount={rowCount ?? currentCompanyList.size} />
          <Bar exportToExcel={() => gridRef.current?.exportToExcel?.()} />
        </StickySubheader>
        <Container maxWidth={false} sx={{ display: 'flex', flex: 1 }}>
          {isGridView ? <GridView ref={gridRef} onRowCountChange={setRowCount} /> : <TileView />}
        </Container>
        <CircularProgressWithBackdrop type="light" isOpen={loading || isSagaDataLoading} />
      </LayoutCompanies>

      <CreateCompany open={isOpenCompanyModal} onClose={toggleCompanyModal} />

      <OrganizationModal />
    </>
  );
};

export default Companies;
